import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Notice from 'components/Shared/Notice'
import { generativeCreativeField } from 'api/models'
import AutoFill from 'components/Recommendations/Campaign/Edit/AutoFill'
import Field from 'components/Recommendations/Campaign/Edit/CampaignForm/Field'
import MediaDropzone from 'components/Recommendations/Campaign/Media/elements/MediaDropzone'
import constants from '../../../../../../constants'
import { useInfoCard } from './useInfoCard'
import { TrashIcon, PlusIcon } from 'images'
import InfoCardHeader from './InfoCardHeader'
import ReadonlyCard from './ReadonlyCard'

const InfoCard = (props) => {
  const {
    images,
    selectedImageId,
    onChange,
    onUpdateCardField,
    onRemoveCard,
    onSelectPreviousCard,
    onSelectNextCard,
    onOpenExpandedImage,
    cardWarnings,
    eventId,
    tc,
    tc_run_id,
    isOpen,
    isRunning,
    readonly,
    uploadStore,
  } = props
  const { t } = useTranslation()
  const cardWarningsMessage = (
    <div>
      {cardWarnings.map((warning) => (
        <div className="mb-1" key={warning}>{warning}</div>
      ))}
    </div>
  )

  const image = images.find((image) => image.id === selectedImageId)
  const isVideoMedia = image?.media?.type?.startsWith?.('video/')
  const dropzoneMedia = image?.media ? [image.media] : []
  const { getRootProps, getInputProps, isMediaLoading, mediaProgress, uploadError, cancelUpload } = useInfoCard({
    image,
    images,
    uploadStore,
    onUpdateCardField,
  })
  const onRemove = () => {
    if (!image?.id || readonly) return
    onUpdateCardField(image.id, 'media', null)
  }
  const handleRemoveCard = () => {
    if (!image?.id || readonly) return
    // If current card is uploading, cancel request before removing card.
    if (isMediaLoading) {
      cancelUpload()
    }
    onRemoveCard?.(image.id)
  }
  const handleSelectPreviousCard = () => {
    if (!image?.id) return
    onSelectPreviousCard?.(image.id)
  }
  const handleSelectNextCard = () => {
    if (!image?.id) return
    onSelectNextCard?.(image.id)
  }
  const handleOpenExpandedImage = () => {
    if (!image?.id || !image?.media?.url) return
    onOpenExpandedImage?.(image.id)
  }
  const handleApplyFieldToAll = (field, value) => {
    if (!images?.length || !onChange) return
    const nextCards = images.map((card) => ({ ...card, [field]: value }))
    onChange(nextCards)
  }

  if (!image) return null

  // return
  return (
    <div className='info-card'>
      <InfoCardHeader
        images={images}
        selectedImageId={selectedImageId}
        onRemoveCard={handleRemoveCard}
        onSelectPreviousCard={handleSelectPreviousCard}
        onSelectNextCard={handleSelectNextCard}
        readonly={readonly}
      />

      {readonly ? 
        <ReadonlyCard images={images} selectedImageId={selectedImageId}/> 
        : 
        (
          <div className='info-card-content'>
            <Notice
              element={cardWarningsMessage}
              isVisible={cardWarnings.length > 0}
              style={{
                backgroundColor: '#fff',
                marginBottom: '6px',
              }}
            />

            {uploadError ? <p className="text-danger">{uploadError}</p> : null}

            <div className='info-card-media'>    
              <MediaDropzone
                media={dropzoneMedia}
                getRootProps={getRootProps}
                getInputProps={getInputProps}
                isMobileSize={false}
                canAddInternal={!readonly && !isMediaLoading}
                isCampaignRunning={isRunning}
                replace={!!image?.media?.url}
              />

              {isMediaLoading ? (
                <div className='info-card-media-image d-flex align-items-center justify-content-center'>
                  <div className="d-flex flex-column align-items-center image-card-image-placeholder">
                    <p className="image-card-image-placeholder-progress">{`${mediaProgress}%`}</p>
                  </div>

                  <span className="icons_remove-icon" onClick={cancelUpload}>
                    <TrashIcon width={14} />
                  </span>
                </div>
              ) : image?.media?.url ? (
                <div className='info-card-media-image'>
                  {isVideoMedia ? (
                    <video
                      src={image.media.url}
                      className="image-card-image"
                      muted
                      playsInline
                      preload="metadata"
                      onClick={handleOpenExpandedImage}
                    />
                  ) : (
                    <img src={image.media.url} alt={image.media.filename} onClick={handleOpenExpandedImage} />
                  )}
                  {!readonly && (
                    <span className="icons_remove-icon">
                      <TrashIcon onClick={onRemove} width={14} />
                    </span>
                  )}
                </div>
              ) : null}
            </div>

            <div className="field-wrapper">
              <Field
                fieldName="headline"
                value={image.headline || ''}
                maxLength={80}
                wrapperClass={isOpen || isRunning ? 'generative-input' : ''}
                inputClass="input-autofill"
                maxLengthClassNamesFn={(length) =>
                  length > 0 && length <= 60 ? 'green' : length > 60 && length <= 70 ? 'orange' : length > 70 ? 'red' : ''
                }
                inputChildren={() =>
                  (isOpen || isRunning) ? (
                    <AutoFill
                      eventId={eventId}
                      tc={tc}
                      tc_run_id={tc_run_id}
                      creativeFieldName={generativeCreativeField.headlines}
                      fields={[image.headline].filter(Boolean)}
                      promptForConfirmation={(image.headline || '').length > 0}
                      onChange={(generatedContent) => {
                        onUpdateCardField(image.id, 'headline', generatedContent[0] || '')
                      }}
                    />
                  ) : null
                }
                tooltip={{
                  title: t('Recommendations.campaign.setup.headline.title'),
                  body: (
                    <div style={{ fontSize: '.875rem', fontWeight: 500 }}>
                      <p className="pb-1">{t('Tips.Recommendations.CreativeComposer.Headline.1')}</p>
                    </div>
                  ),
                }}
                onChange={(payload) => {
                  onUpdateCardField(image.id, 'headline', payload)
                }}
              />

              <button
                type="button"
                className="apply-to-all-button"
                onClick={() => handleApplyFieldToAll('headline', image.headline || '')}
              >
                <PlusIcon width={14} height={14}/>

                <span>
                  {t('Recommendations.campaign.setup.carouselAd.applyToAll')}
                </span>
              </button>
            </div>

            <div className="field-wrapper">
              <Field
                fieldName="description"
                value={image.description || ''}
                maxLength={250}
                wrapperClass={isOpen || isRunning ? 'generative-input' : ''}
                inputClass="input-autofill"
                maxLengthClassNamesFn={(length) =>
                  length > 0 && length <= 200 ? 'green' : length > 200 && length <= 240 ? 'orange' : length > 240 ? 'red' : ''
                }
                inputChildren={() =>
                  (isOpen || isRunning) ? (
                    <AutoFill
                      eventId={eventId}
                      tc={tc}
                      tc_run_id={tc_run_id}
                      creativeFieldName={generativeCreativeField.description}
                      fields={[image.description].filter(Boolean)}
                      promptForConfirmation={(image.description || '').length > 0}
                      onChange={(generatedContent) => {
                        onUpdateCardField(image.id, 'description', generatedContent[0] || '')
                      }}
                    />
                  ) : null
                }
                tooltip={{
                  title: t('Recommendations.campaign.setup.description.title'),
                  body: (
                    <div style={{ fontSize: '.875rem', fontWeight: 500 }}>
                      <p className="pb-1">{t('Tips.Recommendations.CreativeComposer.Description.1')}</p>
                    </div>
                  ),
                }}
                onChange={(payload) => {
                  onUpdateCardField(image.id, 'description', payload)
                }}
              />

              <button
                type="button"
                className="apply-to-all-button"
                onClick={() => handleApplyFieldToAll('description', image.description || '')}
              >
                <PlusIcon width={14} height={14}/>

                <span>
                  {t('Recommendations.campaign.setup.carouselAd.applyToAll')}
                </span>
              </button>
            </div>

            <div className="field-wrapper">
              <Field
                fieldName="card_url"
                value={image.url || ''}
                maxLength={800}
                wrapperClass={isRunning ? 'input-cell--input-padding-min' : 'input-cell--input-padding-max'}
                maxLengthClassNamesFn={(length) =>
                  length > 0 && length <= 600
                    ? 'green'
                    : length > 600 && length <= 700
                      ? 'orange'
                      : length > 700
                        ? 'red'
                        : ''
                }
                validator={(val) => ({
                  isValid: val && constants.URL_VALIDATION_REGEX.test(val.trim()),
                  errorMsg: t('common.errors.wrongUrlFormat'),
                })}
                placeholder="https://www.your-website.com"
                validateOnBlur
                tooltip={{
                  title: t('Tips.Recommendations.CreativeComposer.CardUrl.title'),
                  body: t('Tips.Recommendations.CreativeComposer.CardUrl.body'),
                }}
                onChange={(payload) => {
                  onUpdateCardField(image.id, 'url', payload?.trim?.() ?? payload)
                }}
              />

              <button
                type="button"
                className="apply-to-all-button"
                onClick={() => handleApplyFieldToAll('url', image.url || '')}
              >
                <PlusIcon width={14} height={14}/>

                <span>
                  {t('Recommendations.campaign.setup.carouselAd.applyToAll')}
                </span>
              </button>
            </div>
          </div>
        )}


    </div>
  )
}

export default InfoCard

InfoCard.propTypes = {
  images: PropTypes.array.isRequired,
  selectedImageId: PropTypes.string,
  onChange: PropTypes.func,
  onUpdateCardField: PropTypes.func.isRequired,
  onRemoveCard: PropTypes.func,
  onSelectPreviousCard: PropTypes.func,
  onSelectNextCard: PropTypes.func,
  onOpenExpandedImage: PropTypes.func,
  cardWarnings: PropTypes.arrayOf(PropTypes.string),
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tc: PropTypes.string,
  tc_run_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isOpen: PropTypes.bool,
  isRunning: PropTypes.bool,
  uploadStore: PropTypes.shape({
    uploadCardMedia: PropTypes.func.isRequired,
    cancelUpload: PropTypes.func.isRequired,
    clearUploadError: PropTypes.func.isRequired,
    setUploadErrorByCard: PropTypes.func.isRequired,
    getCardUploadState: PropTypes.func.isRequired,
  }).isRequired,
}